
import React, { useReducer, createContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { storage } from '../constants';
import { getUserSession } from '../api/user';

export const AuthContext = createContext(null);

const initialState = {
    user: null,
    loading: true,
    error: null
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_USER':
            return { ...state, user: action.payload, loading: false, error: null };
        case 'SET_ERROR':
            return { ...state, error: action.payload ?? 'Authentication failed', loading: false, user: false };
        default:
            throw new Error(`Unknown action type: ${action.type}`);
    }
};

export function AuthProvider({ children }) {
    const [authState, authDispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        let isActive = true; // guard against dispatch after unmount

        const checkSession = async () => {
            const token = window.sessionStorage.getItem(storage.TOKEN)?.trim();

            if (!token) {
                // No token: mark unauthenticated - redirect user to container home page
                return;
            }

            try {
                const userSession = await getUserSession(token);
                if (isActive) {
                    authDispatch({ type: 'SET_USER', payload: userSession });
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                if (isActive) {
                    authDispatch({ type: 'SET_ERROR', payload: err?.message });
                }
            }
        };

        checkSession();

        return () => {
            isActive = false;
        };
    }, []); // run once on mount

    // Avoid re-renders by memoizing the context value
    const value = useMemo(() => [authState, authDispatch], [authState]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node
};
